import {
  LayoutDashboard,
  MapPin,
  Users,
  Calendar,
  Building2,
  BarChart3,
  FileText,
  FileSpreadsheet,
  FileCheck,
  Settings,
  Layers,
  HardHat,
  GitBranch,
  UserCheck,
  Router,
  Radio,
  Package,
  Ticket,
  DollarSign,
  Mail,
  AlertTriangle,
  Send,
  ClipboardList,
  HelpCircle,
  CreditCard,
  Receipt,
  Percent,
  Gift,
  Play,
  Clock,
  Cable,
  Network,
  QrCode,
  FileStack,
  Phone,
  MessageCircle,
  ListChecks,
  Plus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ==================== Types ====================

export interface SearchablePage {
  type: 'page';
  name: string;
  href: string;
  icon: LucideIcon;
  keywords: string[];
  permission?: string;
}

export interface SearchableCommand {
  type: 'command';
  name: string;
  href?: string;
  icon: LucideIcon;
  keywords: string[];
  permission?: string;
}

export type SearchableItem = SearchablePage | SearchableCommand;

// ==================== Pages ====================

export const searchablePages: SearchablePage[] = [
  // Main Dashboard
  { type: 'page', name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, keywords: ['home', 'overview', 'main'] },

  // Customers Group
  { type: 'page', name: 'Accounts', href: '/dashboard/accounts', icon: UserCheck, keywords: ['customers', 'subscribers', 'users'], permission: 'accounts.view' },
  { type: 'page', name: 'Leads', href: '/dashboard/leads', icon: Users, keywords: ['prospects', 'potential', 'sales'], permission: 'leads.view' },
  { type: 'page', name: 'Qualification Lists', href: '/dashboard/qualification-lists', icon: ListChecks, keywords: ['qualify', 'prequalify'], permission: 'qualification-lists.view' },
  { type: 'page', name: 'Addresses', href: '/dashboard/addresses', icon: Building2, keywords: ['locations', 'service locations'], permission: 'addresses.view' },
  { type: 'page', name: 'Support Tickets', href: '/dashboard/tickets', icon: Ticket, keywords: ['support', 'help', 'issues', 'problems'], permission: 'tickets.view' },
  { type: 'page', name: 'Campaigns', href: '/dashboard/campaigns', icon: Send, keywords: ['marketing', 'outreach', 'email'], permission: 'campaigns.view' },

  // Operations Group
  { type: 'page', name: 'Scheduling', href: '/dashboard/scheduling', icon: Calendar, keywords: ['appointments', 'calendar', 'schedule'], permission: 'scheduling.view' },
  { type: 'page', name: 'Dispatch Board', href: '/dashboard/dispatch', icon: ClipboardList, keywords: ['dispatch', 'field', 'technicians'], permission: 'dispatch.view' },
  { type: 'page', name: 'Workflow Queue', href: '/dashboard/workflow-queue', icon: GitBranch, keywords: ['installations', 'workflow', 'queue'], permission: 'installations.view' },
  { type: 'page', name: 'Chat Approvals', href: '/dashboard/chat-approvals', icon: MessageCircle, keywords: ['chat', 'approvals', 'messages'], permission: 'chat.approvals.view' },
  { type: 'page', name: 'Technicians', href: '/dashboard/technicians', icon: HardHat, keywords: ['techs', 'installers', 'field workers'], permission: 'users.view' },
  { type: 'page', name: 'Locate Tickets', href: '/dashboard/locate-tickets', icon: Phone, keywords: ['811', 'dig', 'utility', 'underground'], permission: 'locate-tickets.view' },
  { type: 'page', name: 'Outages', href: '/dashboard/outages', icon: AlertTriangle, keywords: ['downtime', 'issues', 'problems', 'network'], permission: 'outages.view' },

  // Network Group
  { type: 'page', name: 'Service Areas', href: '/dashboard/service-areas', icon: MapPin, keywords: ['coverage', 'zones', 'territory'], permission: 'service_areas.view' },
  { type: 'page', name: 'Fiber Mapping', href: '/dashboard/fiber-mapping', icon: Cable, keywords: ['fiber', 'network', 'map', 'routes'], permission: 'fiber.view' },
  { type: 'page', name: 'Parcel Layers', href: '/dashboard/parcel-layers', icon: Layers, keywords: ['parcels', 'properties', 'land'], permission: 'parcels.view' },
  { type: 'page', name: 'Permits', href: '/dashboard/permits', icon: FileStack, keywords: ['permits', 'applications', 'approvals'], permission: 'permits.view' },
  { type: 'page', name: 'Network Sites', href: '/dashboard/network-sites', icon: Radio, keywords: ['towers', 'pops', 'nodes'], permission: 'network-sites.view' },
  { type: 'page', name: 'Topology', href: '/dashboard/network-topology', icon: Network, keywords: ['network diagram', 'connections'], permission: 'network-sites.view' },
  { type: 'page', name: 'IP Addresses', href: '/dashboard/ip-addresses', icon: Network, keywords: ['ip', 'ipam', 'addresses'], permission: 'network-sites.view' },
  { type: 'page', name: 'NMS', href: '/dashboard/nms', icon: Radio, keywords: ['network management', 'monitoring'] },

  // Services Group
  { type: 'page', name: 'Service Tiers', href: '/dashboard/service-tiers', icon: Layers, keywords: ['plans', 'packages', 'speeds'], permission: 'services.view' },
  { type: 'page', name: 'Service Bundles', href: '/dashboard/service-bundles', icon: Package, keywords: ['bundles', 'packages', 'offerings'], permission: 'services.view' },
  { type: 'page', name: 'Site Plans', href: '/dashboard/site-plans', icon: FileText, keywords: ['site plans', 'installation plans'], permission: 'site-plans.view' },
  { type: 'page', name: 'Site Templates', href: '/dashboard/site-templates', icon: ClipboardList, keywords: ['templates', 'site templates'], permission: 'services.view' },
  { type: 'page', name: 'Add-Ons', href: '/dashboard/settings/add-ons', icon: DollarSign, keywords: ['extras', 'additional services'], permission: 'services.view' },

  // Billing Group
  { type: 'page', name: 'Billing Overview', href: '/dashboard/billing', icon: DollarSign, keywords: ['billing', 'payments', 'revenue'], permission: 'billing.view' },
  { type: 'page', name: 'Invoices', href: '/dashboard/invoices', icon: Receipt, keywords: ['bills', 'statements'], permission: 'billing.view' },
  { type: 'page', name: 'Paper Invoices', href: '/dashboard/paper-invoices', icon: Mail, keywords: ['print', 'mail', 'paper bills'], permission: 'billing.view' },
  { type: 'page', name: 'Payments', href: '/dashboard/payments', icon: CreditCard, keywords: ['transactions', 'pay'], permission: 'billing.view' },
  { type: 'page', name: 'Bill Runs', href: '/dashboard/bill-runs', icon: Play, keywords: ['billing cycle', 'generate bills'], permission: 'billing.process' },
  { type: 'page', name: 'Credits', href: '/dashboard/credits', icon: Gift, keywords: ['refunds', 'adjustments'], permission: 'billing.view' },
  { type: 'page', name: 'Tax Rates', href: '/dashboard/tax-rates', icon: Percent, keywords: ['taxes', 'rates'], permission: 'billing.manage' },

  // Inventory Group
  { type: 'page', name: 'Equipment', href: '/dashboard/equipment', icon: Router, keywords: ['inventory', 'devices', 'hardware'], permission: 'equipment.view' },
  { type: 'page', name: 'QR Codes', href: '/dashboard/equipment/qr-codes', icon: QrCode, keywords: ['qr', 'scan', 'labels'], permission: 'equipment.view' },
  { type: 'page', name: 'Materials', href: '/dashboard/materials', icon: Package, keywords: ['supplies', 'inventory'], permission: 'materials.view' },

  // Reports Group
  { type: 'page', name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3, keywords: ['statistics', 'metrics', 'data'], permission: 'reports.view' },
  { type: 'page', name: 'Reports', href: '/dashboard/reports', icon: FileText, keywords: ['reporting', 'export'], permission: 'reports.view' },
  { type: 'page', name: 'AR Aging', href: '/dashboard/reports/ar-aging', icon: Clock, keywords: ['accounts receivable', 'aging', 'collections'], permission: 'reports.view' },
  { type: 'page', name: 'Payment Summary', href: '/dashboard/reports/payment-summary', icon: DollarSign, keywords: ['payments', 'revenue', 'tax', 'quarterly', 'annual'], permission: 'reports.view' },
  { type: 'page', name: 'Operational Reports', href: '/dashboard/operational-reports', icon: FileSpreadsheet, keywords: ['operational', 'field reports'], permission: 'reports.view' },
  { type: 'page', name: 'Compliance', href: '/dashboard/compliance', icon: FileCheck, keywords: ['fcc', 'regulatory'], permission: 'compliance.view' },

  // Settings Group
  { type: 'page', name: 'Settings', href: '/dashboard/settings', icon: Settings, keywords: ['configuration', 'preferences', 'setup'], permission: 'settings.view' },
  { type: 'page', name: 'Departments', href: '/dashboard/departments', icon: Building2, keywords: ['teams', 'groups'], permission: 'settings.view' },
  { type: 'page', name: 'Email Templates', href: '/dashboard/email-templates', icon: Mail, keywords: ['emails', 'templates', 'notifications'], permission: 'settings.view' },
  { type: 'page', name: 'SMS Templates', href: '/dashboard/sms-templates', icon: MessageCircle, keywords: ['text', 'sms', 'messages'], permission: 'sms.view' },
  { type: 'page', name: 'SMS History', href: '/dashboard/sms-history', icon: Phone, keywords: ['text history', 'sms log'], permission: 'sms.view' },
  { type: 'page', name: 'Onboarding Documents', href: '/dashboard/settings/onboarding-documents', icon: FileCheck, keywords: ['documents', 'onboarding'], permission: 'settings.view' },
  { type: 'page', name: 'FAQ & Help', href: '/dashboard/faq', icon: HelpCircle, keywords: ['help', 'documentation', 'faq'], permission: 'settings.view' },

  // User Account
  { type: 'page', name: 'My Account', href: '/dashboard/account', icon: Users, keywords: ['profile', 'user', 'account', 'my account'] },
];

// ==================== Commands ====================

export const searchableCommands: SearchableCommand[] = [
  // Lead Commands
  { type: 'command', name: 'Create Lead', href: '/dashboard/leads?action=new', icon: Plus, keywords: ['new lead', 'add lead', 'create prospect'], permission: 'leads.create' },

  // Ticket Commands
  { type: 'command', name: 'New Support Ticket', href: '/dashboard/tickets?action=new', icon: Ticket, keywords: ['create ticket', 'new ticket', 'support request'], permission: 'tickets.create' },

  // Scheduling Commands
  { type: 'command', name: 'Schedule Installation', href: '/dashboard/scheduling', icon: Calendar, keywords: ['book installation', 'schedule appointment'], permission: 'scheduling.view' },

  // Account Commands
  { type: 'command', name: 'Create Account', href: '/dashboard/accounts?action=new', icon: Plus, keywords: ['new customer', 'add account', 'new subscriber'], permission: 'accounts.create' },

  // Network Commands
  { type: 'command', name: 'Add Network Site', href: '/dashboard/network-sites?action=new', icon: Radio, keywords: ['new site', 'add tower', 'add pop'], permission: 'network-sites.create' },

  // Equipment Commands
  { type: 'command', name: 'Add Equipment', href: '/dashboard/equipment?action=new', icon: Router, keywords: ['new equipment', 'add device'], permission: 'equipment.create' },

  // Outage Commands
  { type: 'command', name: 'Report Outage', href: '/dashboard/outages?action=new', icon: AlertTriangle, keywords: ['new outage', 'report downtime'], permission: 'outages.create' },

  // Locate Ticket Commands
  { type: 'command', name: 'Create Locate Ticket', href: '/dashboard/locate-tickets?action=new', icon: Phone, keywords: ['811', 'new locate', 'dig request'], permission: 'locate-tickets.create' },

  // Service Area Commands
  { type: 'command', name: 'Add Service Area', href: '/dashboard/service-areas?action=new', icon: MapPin, keywords: ['new area', 'add zone', 'new territory'], permission: 'service_areas.create' },

  // Campaign Commands
  { type: 'command', name: 'Create Campaign', href: '/dashboard/campaigns?action=new', icon: Send, keywords: ['new campaign', 'email campaign', 'marketing'], permission: 'campaigns.create' },
];

// ==================== Helper Functions ====================

/**
 * Get all searchable items (pages + commands)
 */
export function getAllSearchableItems(): SearchableItem[] {
  return [...searchablePages, ...searchableCommands];
}

/**
 * Simple fuzzy search matching
 * Returns true if all query parts are found in the searchable text
 */
export function fuzzyMatch(query: string, text: string, keywords: string[] = []): boolean {
  const normalizedQuery = query.toLowerCase().trim();
  const normalizedText = text.toLowerCase();
  const normalizedKeywords = keywords.map(k => k.toLowerCase()).join(' ');
  const searchableText = `${normalizedText} ${normalizedKeywords}`;

  // Split query into words and check if all are present
  const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
  return queryWords.every(word => searchableText.includes(word));
}

/**
 * Filter searchable items by query
 */
export function filterSearchableItems(
  items: SearchableItem[],
  query: string,
  hasPermission: (permission: string) => boolean
): SearchableItem[] {
  if (!query.trim()) {
    // Return all items when no query, filtered by permission
    return items.filter(item => !item.permission || hasPermission(item.permission));
  }

  return items.filter(item => {
    // Check permission first
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }
    // Then check fuzzy match
    return fuzzyMatch(query, item.name, item.keywords);
  });
}

/**
 * Score a search result for ranking
 * Higher score = better match
 */
export function scoreSearchResult(query: string, item: SearchableItem): number {
  const normalizedQuery = query.toLowerCase().trim();
  const normalizedName = item.name.toLowerCase();

  // Exact match gets highest score
  if (normalizedName === normalizedQuery) return 100;

  // Starts with query gets high score
  if (normalizedName.startsWith(normalizedQuery)) return 80;

  // Name contains query gets medium-high score
  if (normalizedName.includes(normalizedQuery)) return 60;

  // Keyword exact match
  if (item.keywords.some(k => k.toLowerCase() === normalizedQuery)) return 50;

  // Keyword contains
  if (item.keywords.some(k => k.toLowerCase().includes(normalizedQuery))) return 30;

  // Default fuzzy match
  return 10;
}

/**
 * Sort search results by relevance
 */
export function sortByRelevance(items: SearchableItem[], query: string): SearchableItem[] {
  return [...items].sort((a, b) => {
    const scoreA = scoreSearchResult(query, a);
    const scoreB = scoreSearchResult(query, b);
    return scoreB - scoreA;
  });
}
